import { db } from '../lib/firebase';
import { POD_COLLECTION, POD_COLLECTION_IS_V2 } from '../config/collections';
import { getMeasurementsInRange } from './podMeasurements';
import { collection, query, where, getDocs, orderBy, Timestamp, collectionGroup, documentId } from 'firebase/firestore';

export interface MsconsReading {
  podId: string;
  month: string; // YYYY-MM formátum
  consumption: number; // kWh
  timestamp: Date;
  provider: string;
  dataType: 'monthly';
  source: 'MSCONS' | 'REGISZTER';
}

export interface MsconsSummary {
  podId: string;
  podName?: string;
  provider: string;
  monthlyReadings: {
    month: string;
    consumption: number;
    timestamp: Date;
  }[];
  totalConsumption: number;
  averageMonthly: number;
}

class MsconsService {
  /**
   * Havi elosztói elszámolások lekérdezése minden POD-ra egyben.
   *  v1 (energy_pods):    collectionGroup('measurements') where data_type='monthly'
   *  v2 (energy_pods_v2): collectionGroup('mscons') – csak a v2 gyökér alatti dokumentumok (doc path szűrés)
   */
  private monthlyMsconsQuery(startDate?: Date, endDate?: Date) {
    if (POD_COLLECTION_IS_V2) {
      // A collectionGroup minden 'mscons' alkollekciót lát (a régi energy_pods alattiakat is) –
      // az útvonal-tartománnyal a v2 gyökérre szűkítjük.
      let q = query(
        collectionGroup(db, 'mscons'),
        orderBy(documentId()),
        where(documentId(), '>=', `${POD_COLLECTION}/`),
        where(documentId(), '<', `${POD_COLLECTION}0`)
      );
      return q;
    }
    let q = query(collectionGroup(db, 'measurements'), where('data_type', '==', 'monthly'), orderBy('timestamp', 'desc'));
    if (startDate) q = query(q, where('timestamp', '>=', Timestamp.fromDate(startDate)));
    if (endDate) q = query(q, where('timestamp', '<=', Timestamp.fromDate(endDate)));
    return q;
  }

  /** Egy havi elszámolás-dokumentum → egységes {podId, timestamp, consumption, provider, data_type} (v1/v2). */
  private readMonthlyDoc(doc: any): { podId: string; timestamp: any; consumption: number; provider: string; data_type: string } | null {
    const data = doc.data();
    const podId: string | undefined = doc.ref.parent.parent?.id;
    if (!podId) return null;
    if (!POD_COLLECTION_IS_V2) {
      return { podId, timestamp: data.timestamp, consumption: data.consumption || 0, provider: data.provider || 'Unknown', data_type: data.data_type };
    }
    const pe = String(data.billing_period_end || '');
    if (pe.length !== 8) return null;
    const ts = new Date(Number(pe.slice(0, 4)), Number(pe.slice(4, 6)) - 1, Number(pe.slice(6, 8)));
    return { podId, timestamp: Timestamp.fromDate(ts), consumption: Number(data.total_consumption_kwh) || 0,
             provider: data.provider || 'Unknown', data_type: 'monthly' };
  }

  /**
   * Havi MSCONS adatok lekérdezése egy POD-hoz
   */
  async getMsconsReadings(
    podId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<MsconsReading[]> {
    try {
      if (POD_COLLECTION_IS_V2) {
        // v2: az elosztói elszámolások az mscons/ alkollekcióban vannak (doc id: YYYY-MM_<dokumentumszám>)
        const snap = await getDocs(collection(db, POD_COLLECTION, podId, 'mscons'));
        const out: MsconsReading[] = [];
        snap.forEach(d => {
          const x = d.data();
          const pe = String(x.billing_period_end || '');
          if (pe.length !== 8) return;
          const ts = new Date(Number(pe.slice(0, 4)), Number(pe.slice(4, 6)) - 1, Number(pe.slice(6, 8)));
          if (startDate && ts < startDate) return;
          if (endDate && ts > endDate) return;
          out.push({
            podId, month: this.formatMonth(ts), consumption: Number(x.total_consumption_kwh) || 0, timestamp: ts,
            provider: x.provider || 'Unknown', dataType: 'monthly' as const, source: 'MSCONS' as const,
          });
        });
        return out.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      }
      // v1: a régi uploader a havi MSCONS-értéket is a measurements alkollekcióba tette (data_type='monthly')
      const rows: any[] = await getMeasurementsInRange(podId, startDate || new Date(2000, 0, 1), endDate || new Date(2100, 0, 1));
      const monthlyReadings = rows
        .map(data => {
          const timestamp = data.timestamp?.toDate() || new Date();
          return {
            podId,
            month: this.formatMonth(timestamp),
            consumption: data.consumption || 0,
            timestamp,
            provider: data.provider || 'Unknown',
            dataType: 'monthly' as const,
            source: (data.source || 'MSCONS') as 'MSCONS' | 'REGISZTER',
            rawDataType: data.data_type
          };
        })
        .filter(reading => reading.rawDataType === 'monthly');
      
      return monthlyReadings;
    } catch (error) {
      console.error('Error fetching MSCONS readings:', error);
      console.error('POD ID:', podId);
      return []; // Üres tömböt adunk vissza hiba esetén
    }
  }

  /**
   * Kiválasztott POD-ok MSCONS összesítése - GYORS collectionGroup query
   */
  async getAllMsconsSummariesForPods(
    podIds?: string[],
    startDate?: Date,
    endDate?: Date
  ): Promise<MsconsSummary[]> {
    try {
      console.log('🚀 FAST getAllMsconsSummariesForPods called with:', { podIds, startDate, endDate });
      const startTime = performance.now();
      
      // CollectionGroup query - EGY lekérdezés az ÖSSZES measurements-re!
      // v1: collectionGroup('measurements') data_type='monthly'; v2: collectionGroup('mscons') az energy_pods_v2 alatt
      let measurementsQuery = this.monthlyMsconsQuery(startDate, endDate);

      console.log('📡 Fetching monthly measurements...');
      const measurementsSnapshot = await getDocs(measurementsQuery);
      console.log(`📊 Found ${measurementsSnapshot.docs.length} monthly measurements`);

      // Csoportosítás POD szerint
      const podMap = new Map<string, {
        podId: string;
        provider: string;
        readings: { month: string; consumption: number; timestamp: Date }[];
      }>();

      for (const doc of measurementsSnapshot.docs) {
        const rec = this.readMonthlyDoc(doc);
        if (!rec) continue;
        if (POD_COLLECTION_IS_V2) {   // v2: a dátumszűrés itt, mert a lekérdezés útvonal szerint megy
          const t = rec.timestamp.toDate();
          if ((startDate && t < startDate) || (endDate && t > endDate)) continue;
        }
        const data = rec as any;
        const podId = rec.podId;

        // POD ID szűrés - ha meg van adva POD lista, csak azokat dolgozzuk fel
        if (podIds && podIds.length > 0 && !podIds.includes(podId)) {
          continue;
        }

        const timestamp = data.timestamp?.toDate() || new Date();
        const consumption = data.consumption || 0;
        const providerName = data.provider || 'Unknown';

        if (!podMap.has(podId)) {
          podMap.set(podId, {
            podId,
            provider: providerName,
            readings: []
          });
        }

        podMap.get(podId)!.readings.push({
          month: this.formatMonth(timestamp),
          consumption,
          timestamp
        });
      }

      console.log(`🏢 Found ${podMap.size} unique PODs with monthly data`);

      // Összesítések létrehozása
      const summaries: MsconsSummary[] = [];
      
      for (const [podId, podData] of Array.from(podMap.entries())) {
        const totalConsumption = podData.readings.reduce((sum: number, r: { consumption: number }) => sum + r.consumption, 0);
        const averageMonthly = totalConsumption / podData.readings.length;

        summaries.push({
          podId,
          podName: podId,
          provider: podData.provider,
          monthlyReadings: podData.readings.sort((a: { month: string }, b: { month: string }) => b.month.localeCompare(a.month)),
          totalConsumption,
          averageMonthly
        });
      }

      const endTime = performance.now();
      console.log(`🎉 Created ${summaries.length} summaries in ${(endTime - startTime).toFixed(0)}ms`);
      
      return summaries.sort((a, b) => b.totalConsumption - a.totalConsumption);
    } catch (error) {
      console.error('❌ Error fetching MSCONS summaries:', error);
      throw error;
    }
  }

  /**
   * Összes POD MSCONS összesítése - GYORS collectionGroup query (DEPRECATED - use getAllMsconsSummariesForPods)
   */
  async getAllMsconsSummaries(
    provider?: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<MsconsSummary[]> {
    try {
      console.log('� FAST getAllMsconsSummaries called with:', { provider, startDate, endDate });
      const startTime = performance.now();
      
      // ÚJ MEGOLDÁS: CollectionGroup query - EGY lekérdezés az ÖSSZES measurements-re!
      // v1: collectionGroup('measurements') data_type='monthly'; v2: collectionGroup('mscons') az energy_pods_v2 alatt
      let measurementsQuery = this.monthlyMsconsQuery(startDate, endDate);

      console.log('📡 Fetching all monthly measurements in ONE query...');
      const measurementsSnapshot = await getDocs(measurementsQuery);
      console.log(`� Found ${measurementsSnapshot.docs.length} monthly measurements`);

      // Csoportosítás POD szerint
      const podMap = new Map<string, {
        podId: string;
        provider: string;
        readings: { month: string; consumption: number; timestamp: Date }[];
      }>();

      for (const doc of measurementsSnapshot.docs) {
        const rec = this.readMonthlyDoc(doc);
        if (!rec) continue;
        if (POD_COLLECTION_IS_V2) {   // v2: a dátumszűrés itt, mert a lekérdezés útvonal szerint megy
          const t = rec.timestamp.toDate();
          if ((startDate && t < startDate) || (endDate && t > endDate)) continue;
        }
        const data = rec as any;
        const podId = rec.podId;

        const timestamp = data.timestamp?.toDate() || new Date();
        const consumption = data.consumption || 0;
        const providerName = data.provider || 'Unknown';

        // Provider szűrés
        if (provider && providerName !== provider) {
          continue;
        }

        if (!podMap.has(podId)) {
          podMap.set(podId, {
            podId,
            provider: providerName,
            readings: []
          });
        }

        podMap.get(podId)!.readings.push({
          month: this.formatMonth(timestamp),
          consumption,
          timestamp
        });
      }

      console.log(`🏢 Found ${podMap.size} unique PODs with monthly data`);

      // POD információk lekérdezése (név, stb.)
      const summaries: MsconsSummary[] = [];
      
      for (const [podId, podData] of Array.from(podMap.entries())) {
        const totalConsumption = podData.readings.reduce((sum: number, r: { consumption: number }) => sum + r.consumption, 0);
        const averageMonthly = totalConsumption / podData.readings.length;

        summaries.push({
          podId,
          podName: podId, // Később lekérdezhetjük a nevet, ha kell
          provider: podData.provider,
          monthlyReadings: podData.readings.sort((a: { month: string }, b: { month: string }) => b.month.localeCompare(a.month)),
          totalConsumption,
          averageMonthly
        });
      }

      const endTime = performance.now();
      console.log(`🎉 Created ${summaries.length} summaries in ${(endTime - startTime).toFixed(0)}ms`);
      
      return summaries.sort((a, b) => b.totalConsumption - a.totalConsumption);
    } catch (error) {
      console.error('❌ Error fetching MSCONS summaries:', error);
      throw error;
    }
  }

  /**
   * Havi összesítés provider szerint
   */
  async getProviderMonthlySummary(
    provider: string,
    year: number
  ): Promise<{ month: string; totalConsumption: number; podCount: number }[]> {
    try {
      const startDate = new Date(year, 0, 1);
      const endDate = new Date(year, 11, 31);

      const summaries = await this.getAllMsconsSummaries(provider, startDate, endDate);

      // Havi bontás
      const monthlyMap = new Map<string, { consumption: number; pods: Set<string> }>();

      summaries.forEach(summary => {
        summary.monthlyReadings.forEach(reading => {
          const existing = monthlyMap.get(reading.month) || { 
            consumption: 0, 
            pods: new Set<string>() 
          };
          
          existing.consumption += reading.consumption;
          existing.pods.add(summary.podId);
          
          monthlyMap.set(reading.month, existing);
        });
      });

      // Rendezés és formázás
      return Array.from(monthlyMap.entries())
        .map(([month, data]) => ({
          month,
          totalConsumption: data.consumption,
          podCount: data.pods.size
        }))
        .sort((a, b) => a.month.localeCompare(b.month));
    } catch (error) {
      console.error('Error fetching provider monthly summary:', error);
      throw error;
    }
  }

  /**
   * MSCONS vs Napi adatok összehasonlítása
   */
  async compareMsconsWithDaily(
    podId: string,
    month: string // YYYY-MM
  ): Promise<{
    mscons: number;
    daily: number;
    difference: number;
    percentageDiff: number;
  }> {
    try {
      const [year, monthNum] = month.split('-').map(Number);
      const startDate = new Date(year, monthNum - 1, 1);
      const endDate = new Date(year, monthNum, 0);

      // MSCONS adat
      const msconsReadings = await this.getMsconsReadings(podId, startDate, endDate);
      const msconsTotal = msconsReadings.reduce((sum, r) => sum + r.consumption, 0);

      // Napi összesítések
      const dailySummariesRef = collection(db, POD_COLLECTION, podId, 'daily_summaries');
      const dailyQuery = query(
        dailySummariesRef,
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate))
      );

      const dailySnapshot = await getDocs(dailyQuery);
      const dailyTotal = dailySnapshot.docs.reduce((sum, doc) => {
        return sum + (doc.data().total_consumption || 0);
      }, 0);

      const difference = msconsTotal - dailyTotal;
      const percentageDiff = dailyTotal > 0 ? (difference / dailyTotal) * 100 : 0;

      return {
        mscons: msconsTotal,
        daily: dailyTotal,
        difference,
        percentageDiff
      };
    } catch (error) {
      console.error('Error comparing MSCONS with daily:', error);
      throw error;
    }
  }

  /**
   * Hónap formázása YYYY-MM formátumra
   */
  private formatMonth(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  /**
   * Hónap név magyar formátumban
   */
  getMonthName(month: string): string {
    const [year, monthNum] = month.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1);
    
    return date.toLocaleDateString('hu-HU', { 
      year: 'numeric', 
      month: 'long' 
    });
  }
}

export const msconsService = new MsconsService();
